import { supabase } from '../lib/supabase';

interface ParsedAddress {
  number: string;
  streetName: string;
  streetType: string;
  directional?: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
}

class PropertyTokenGenerator {
  
  /**
   * Generate a unique property token from address and zip code
   * Example: "9 Boxwood Street East Hampton NY 11937" → "9BOXWST11937"
   */
  async generateToken(address: string, zip: string): Promise<string> {
    try {
      const parsed = this.parseAddress(address);
      const baseToken = this.createBaseToken(parsed, zip);
      
      // Ensure uniqueness
      return await this.ensureUniqueToken(baseToken, address, zip);
    } catch (error) {
      console.error('Error generating property token:', error);
      // Fallback to random token
      return this.generateRandomToken();
    }
  }

  /**
   * Save token to existing property search record
   */
  async saveTokenToProperty(propertySearchId: string, token: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('property_searches')
        .update({ 
          property_token: token,
          token_generated_at: new Date().toISOString()
        })
        .eq('id', propertySearchId);

      if (error) {
        console.error('Error saving token to property:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in saveTokenToProperty:', error);
      return false;
    }
  }

  /**
   * Retrieve property by token (now works with public RLS policy)
   * Also handles access codes from scraped_properties via bridging
   */
  async getPropertyByToken(token: string): Promise<any | null> {
    try {
      console.log('Looking up property for token:', token);
      
      const { data, error } = await supabase
        .from('property_searches')
        .select(`
          id,
          address,
          master_image_url,
          selected_image_url,
          watercolor_image_url,
          pencil_image_url,
          oil_image_url,
          charcoal_image_url,
          chalk_image_url,
          created_at,
          first_opened_at,
          parent_property_id,
          is_clone,
          property_token,
          address_line_1,
          address_line_2,
          city,
          state,
          zip_code,
          country
        `)
        .eq('property_token', token.toUpperCase())
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching property by token:', error);
        console.log('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        // If not found in property_searches, this might be an access code
        // The bridgeAccessCodeToPropertySearch function will handle the fallback
        return null;
      }

      if (data) {
        console.log('Property found for token:', token, {
          id: data.id,
          address: data.address,
          hasMasterImage: !!data.master_image_url,
          hasWatercolor: !!data.watercolor_image_url,
          token: data.property_token
        });
        return data;
      } else {
        console.log('No property found for token:', token);
        return null;
      }
    } catch (error) {
      console.error('Error in getPropertyByToken:', error);
      return null;
    }
  }

  /**
   * Universal property retrieval - handles both tokens and access codes
   * This is the main entry point that should be used by the app
   */
  async getPropertyByTokenOrAccessCode(codeOrToken: string): Promise<any | null> {
    return await this.bridgeAccessCodeToPropertySearch(codeOrToken);
  }

  /**
   * Generate the full direct URL for a property token
   */
  generateDirectUrl(token: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/${token.toUpperCase()}`;
  }

  /**
   * Generate the full direct URL for an access code (same pattern as tokens)
   */
  generateAccessCodeUrl(accessCode: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/${accessCode.toUpperCase()}`;
  }

  /**
   * Generate token and return both token and direct URL
   */
  async generateTokenWithUrl(address: string, zip: string): Promise<{
    token: string;
    directUrl: string;
  }> {
    const token = await this.generateToken(address, zip);
    const directUrl = this.generateDirectUrl(token);
    
    return {
      token,
      directUrl
    };
  }

  /**
   * Generate a temporary token for image uploads without address
   * Format: IMG{timestamp}{random} e.g. IMG1704730000AB
   */
  async generateTemporaryToken(): Promise<string> {
    try {
      // Use timestamp + 2 random chars for uniqueness
      const timestamp = Date.now().toString(36).toUpperCase();
      const randomChars = this.generateRandomString(2);
      const tempToken = `IMG${timestamp}${randomChars}`;
      
      // Ensure uniqueness
      const exists = await this.tokenExists(tempToken);
      if (exists) {
        // Add extra random char if collision
        return `${tempToken}${this.generateRandomString(1)}`;
      }
      
      return tempToken;
    } catch (error) {
      console.error('Error generating temporary token:', error);
      return this.generateRandomToken();
    }
  }

  /**
   * Update existing property search from temporary token to address-based token
   */
  async updateToAddressToken(propertySearchId: string, address: string, zip: string): Promise<{
    success: boolean;
    newToken?: string;
    oldToken?: string;
  }> {
    try {
      // Get current property data
      const { data: property, error: fetchError } = await supabase
        .from('property_searches')
        .select('property_token')
        .eq('id', propertySearchId)
        .limit(1)
        .single();

      if (fetchError || !property) {
        console.error('Error fetching property for token update:', fetchError);
        return { success: false };
      }

      const oldToken = property.property_token;
      
      // Generate new address-based token
      const newToken = await this.generateToken(address, zip);
      
      // Update property with new token
      const { error: updateError } = await supabase
        .from('property_searches')
        .update({ 
          property_token: newToken,
          token_generated_at: new Date().toISOString()
        })
        .eq('id', propertySearchId);

      if (updateError) {
        console.error('Error updating property token:', updateError);
        return { success: false };
      }

      console.log(`✅ Updated token: ${oldToken} → ${newToken}`);
      return { 
        success: true, 
        newToken, 
        oldToken 
      };
    } catch (error) {
      console.error('Error in updateToAddressToken:', error);
      return { success: false };
    }
  }

  /**
   * Retrieve scraped property by access code (for campaign properties)
   */
  async getScrapedPropertyByAccessCode(accessCode: string): Promise<any | null> {
    try {
      console.log('🔍 [SCRAPED] Looking up scraped property for access code:', accessCode);
      
      const { data, error } = await supabase
        .from('scraped_properties')
        .select(`
          id,
          address_line_1,
          address_line_2,
          city,
          state,
          zip_code,
          watercolor_image_url,
          oil_image_url,
          pencil_image_url,
          charcoal_image_url,
          homes_main_image_url,
          zillow_image_url,
          buyers_agent_name,
          buyers_agent_email,
          created_at,
          access_code,
          campaign_id,
          raw_data
        `)
        .eq('access_code', accessCode.toUpperCase())
        .limit(1)
        .single();

      if (error) {
        console.error('❌ [SCRAPED] Error fetching scraped property by access code:', error);
        console.log('❌ [SCRAPED] Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return null;
      }

      if (data) {
        console.log('✅ [SCRAPED] Scraped property found for access code:', accessCode);
        console.log('📋 [SCRAPED] Property details:', {
          id: data.id,
          address: data.address_line_1,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code,
          hasWatercolor: !!data.watercolor_image_url,
          hasOil: !!data.oil_image_url,
          hasPencil: !!data.pencil_image_url,
          hasCharcoal: !!data.charcoal_image_url,
          hasZillowImage: !!data.zillow_image_url,
          hasHomesImage: !!data.homes_main_image_url,
          accessCode: data.access_code
        });
        return data;
      } else {
        console.log('❌ [SCRAPED] No scraped property found for access code:', accessCode);
        return null;
      }
    } catch (error) {
      console.error('❌ [SCRAPED] Error in getScrapedPropertyByAccessCode:', error);
      return null;
    }
  }

  /**
   * Bridge access code to property search workflow
   * If token is an access code from scraped_properties, create a property_searches record
   */
  async bridgeAccessCodeToPropertySearch(codeOrToken: string): Promise<{ property: any; fromBridge?: boolean; accessCode?: string } | null> {
    try {
      console.log('🔍 [BRIDGE] Starting bridge process for:', codeOrToken);
      
      // First, check if it's a regular property token
      console.log('🔍 [BRIDGE] Step 1: Checking property_searches table...');
      const existingProperty = await this.getPropertyByToken(codeOrToken);
      if (existingProperty) {
        console.log('✅ [BRIDGE] Found existing property_searches record:', existingProperty.id);
        return { property: existingProperty, fromBridge: false };
      }
      
      console.log('❌ [BRIDGE] Not found in property_searches, calling bridge edge function...');
      
      // Call the edge function to handle the bridging server-side
      console.log('🔍 [BRIDGE] Step 2: Calling bridge-access-code edge function...');
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bridge-access-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          accessCode: codeOrToken
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [BRIDGE] Error from edge function:', errorData);
        
        if (response.status === 404) {
          console.log('❌ [BRIDGE] No property found for access code:', codeOrToken);
          return null;
        }
        
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        console.log('❌ [BRIDGE] Edge function returned failure:', result.error);
        return null;
      }

      console.log('✅ [BRIDGE] Edge function succeeded:', {
        propertyId: result.property.id,
        propertyToken: result.property.property_token,
        address: result.property.address,
        originalAccessCode: result.originalAccessCode,
        fromBridge: result.fromBridge
      });
      
      console.log('🎉 [BRIDGE] Bridge process completed successfully!');
      
      return {
        property: result.property,
        fromBridge: result.fromBridge,
        accessCode: result.accessCode
      };
      
    } catch (error) {
      console.error('❌ [BRIDGE] Error in bridge process:', error);
      return null;
    }
  }

  private parseAddress(address: string): ParsedAddress {
    // Simple address parser - can be enhanced with a proper library
    const parts = address.trim().split(/\s+/);
    const number = parts[0] || '';
    
    // Find zip code (5 digits at the end)
    const zipMatch = address.match(/\b\d{5}\b/);
    const zip = zipMatch ? zipMatch[0] : '';
    
    // Extract street name (everything between number and type/zip)
    const addressWithoutNumber = address.replace(number, '').trim();
    const addressWithoutZip = addressWithoutNumber.replace(zip, '').trim();
    
    // Common street types
    const streetTypes = ['STREET', 'ST', 'AVENUE', 'AVE', 'ROAD', 'RD', 'DRIVE', 'DR', 
                        'TERRACE', 'TER', 'LANE', 'LN', 'COURT', 'CT', 'PLACE', 'PL'];
    
    let streetType = 'STREET';
    let streetName = addressWithoutZip;
    
    // Find street type
    for (const type of streetTypes) {
      const regex = new RegExp(`\\b${type}\\b`, 'i');
      if (regex.test(addressWithoutZip)) {
        streetType = type;
        streetName = addressWithoutZip.replace(regex, '').trim();
        break;
      }
    }
    
    // Extract city and state (rough approximation)
    const cityStateMatch = address.match(/([A-Za-z\s]+)\s+([A-Z]{2})\s+\d{5}/);
    const city = cityStateMatch ? cityStateMatch[1].trim() : '';
    const state = cityStateMatch ? cityStateMatch[2] : '';

    return {
      number: number.replace(/[^0-9]/g, ''),
      streetName: streetName.replace(/[^A-Za-z0-9\s]/g, '').trim(),
      streetType: streetType,
      city,
      state,
      zip
    };
  }

  private createBaseToken(parsed: ParsedAddress, zip: string): string {
    const houseNum = parsed.number;
    const streetName = this.normalizeStreetName(parsed.streetName);
    const streetType = this.getStreetTypeCode(parsed.streetType);
    
    // Format: {number}{4-chars-street}{2-char-type}{zip}
    const streetPart = streetName.substring(0, 4).toUpperCase();
    return `${houseNum}${streetPart}${streetType}${zip}`;
  }

  private normalizeStreetName(name: string): string {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')       // Remove special chars
      .replace(/SAINT/g, 'ST')         // St. vs Saint
      .replace(/\bNORTH\b/g, 'N')      // Directional shortcuts
      .replace(/\bSOUTH\b/g, 'S')
      .replace(/\bEAST\b/g, 'E')
      .replace(/\bWEST\b/g, 'W');
  }

  private getStreetTypeCode(type: string): string {
    const codes: { [key: string]: string } = {
      'STREET': 'ST', 'ROAD': 'RD', 'AVENUE': 'AV', 'DRIVE': 'DR',
      'TERRACE': 'TR', 'LANE': 'LN', 'COURT': 'CT', 'PLACE': 'PL',
      'CIRCLE': 'CR', 'WAY': 'WY', 'BOULEVARD': 'BL', 'PARKWAY': 'PK',
      'ST': 'ST', 'RD': 'RD', 'AVE': 'AV', 'DR': 'DR',
      'TER': 'TR', 'LN': 'LN', 'CT': 'CT', 'PL': 'PL'
    };
    return codes[type.toUpperCase()] || 'ST';
  }

  private async ensureUniqueToken(baseToken: string, address: string, zip: string): Promise<string> {
    const exists = await this.tokenExists(baseToken);
    
    if (!exists) {
      return baseToken;
    }

    // Handle collision with incremental suffixes
    return await this.handleCollision(baseToken, address, zip);
  }

  private async tokenExists(token: string): Promise<boolean> {
    try {
      const { data } = await supabase
        .from('property_searches')
        .select('id')
        .eq('property_token', token.toUpperCase())
        .limit(1);
      
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking token existence:', error);
      return false;
    }
  }

  private async handleCollision(baseToken: string, address: string, zip: string): Promise<string> {
    // Strategy 1: Add single character hash
    const hash1 = this.generateSimpleHash(address + zip).charAt(0);
    const token1 = `${baseToken}${hash1}`;
    
    if (!(await this.tokenExists(token1))) {
      return token1;
    }

    // Strategy 2: Add 2-character hash
    const hash2 = this.generateSimpleHash(address + zip + 'collision');
    const token2 = `${baseToken}${hash2.substring(0, 2)}`;
    
    if (!(await this.tokenExists(token2))) {
      return token2;
    }

    // Strategy 3: Add timestamp-based suffix
    const timestamp = Date.now().toString(36).slice(-2).toUpperCase();
    return `${baseToken}${timestamp}`;
  }

  private generateSimpleHash(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36).toUpperCase();
  }

  private generateRandomToken(): string {
    // Fallback random token
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Generate random string of specified length
   */
  private generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}

// Export singleton instance
export const propertyTokenGenerator = new PropertyTokenGenerator(); 